import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import {
  Heart,
  ShoppingBag,
  Trash2,
  LayoutGrid,
  List,
  ChevronDown,
  ArrowUpDown,
  Check
} from 'lucide-react';
import { fetchWishlistFromBackend, removeFromWishlistBackend, getProductImageUrl } from '../services/api';

const UserWishlist = ({ cartItems = [], onAddToCart, onUpdateQuantity }) => {
  const [wishlistItems, setWishlistItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState('list'); // 'list' | 'grid'
  const [sortBy, setSortBy] = useState('recently'); // 'recently' | 'price-low' | 'price-high'
  const [sortDropdownOpen, setSortDropdownOpen] = useState(false);

  const loadWishlist = async () => {
    setLoading(true);
    try {
      const data = await fetchWishlistFromBackend();
      if (data && Array.isArray(data)) {
        setWishlistItems(data);
      } else {
        setWishlistItems([]);
      }
    } catch (e) {
      setWishlistItems([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadWishlist();
  }, []);

  // Dynamic 3 Sort Options Logic
  const sortedItems = useMemo(() => {
    const list = [...wishlistItems];
    if (sortBy === 'price-low') {
      return list.sort((a, b) => Number(a.price || 0) - Number(b.price || 0));
    }
    if (sortBy === 'price-high') {
      return list.sort((a, b) => Number(b.price || 0) - Number(a.price || 0));
    }
    // Default: 'recently' (Recently Added)
    return list;
  }, [wishlistItems, sortBy]);

  const handleRemove = async (productId) => {
    setWishlistItems((prev) => prev.filter((item) => item.id !== productId));
    await removeFromWishlistBackend(productId);
  };

  const handleMoveAllToCart = () => {
    if (onAddToCart && wishlistItems.length > 0) {
      wishlistItems.forEach((item) => onAddToCart(item));
    }
  };

  const sortLabels = {
    recently: 'Recently Added',
    'price-low': 'Price Low to High',
    'price-high': 'Price High to Low'
  };

  return (
    <div style={{ minHeight: '100vh', background: '#fafbfc', padding: '60px 56px', fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
      <div style={{ maxWidth: '1180px', margin: '0 auto' }}>
        
        {/* HEADER ROW MATCHING wishlist.png 1:1 */}
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: '20px', marginBottom: '32px' }}>
          <div>
            <h1 style={{ fontSize: '36px', fontWeight: 900, color: '#0f172a', letterSpacing: '-0.02em', margin: '0 0 6px 0' }}>
              My <span style={{ color: '#dc2626' }}>Wellness Collection</span>
            </h1>
            <p style={{ fontSize: '14.5px', color: '#64748b', margin: 0 }}>
              Panels you've saved for your wellness journey.
            </p>
          </div>

          {/* Right Header Action Controls */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap' }}>
            
            {/* Saved Items Count Badge */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13.5px', fontWeight: 800, color: '#dc2626', background: '#fef2f2', padding: '10px 18px', borderRadius: '9999px', border: '1px solid #fee2e2' }}>
              <Heart size={16} fill="#dc2626" />
              <span>{wishlistItems.length} Saved Items</span>
            </div>

            {/* Move All to Cart Button */}
            <button
              onClick={handleMoveAllToCart}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '8px',
                background: '#ffffff',
                border: '1px solid #fca5a5',
                color: '#dc2626',
                padding: '10px 20px',
                borderRadius: '10px',
                fontSize: '13.5px',
                fontWeight: 800,
                cursor: 'pointer',
                boxShadow: '0 2px 6px rgba(0,0,0,0.02)'
              }}
            >
              <ShoppingBag size={16} />
              <span>Move All to Cart</span>
            </button>

            {/* CUSTOM STYLED 3-OPTION SORT DROPDOWN */}
            <div style={{ position: 'relative' }}>
              <button
                onClick={() => setSortDropdownOpen(!sortDropdownOpen)}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '8px',
                  background: '#ffffff',
                  border: '1px solid #cbd5e1',
                  borderRadius: '10px',
                  padding: '10px 16px',
                  fontSize: '13px',
                  fontWeight: 700,
                  color: '#0f172a',
                  cursor: 'pointer',
                  boxShadow: '0 2px 6px rgba(0,0,0,0.02)'
                }}
              >
                <ArrowUpDown size={14} color="#64748b" />
                <span>Sort by: <strong>{sortLabels[sortBy]}</strong></span>
                <ChevronDown size={14} color="#64748b" style={{ transform: sortDropdownOpen ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s ease' }} />
              </button>

              {/* Sort Menu Popover */}
              {sortDropdownOpen && (
                <div
                  style={{
                    position: 'absolute',
                    top: 'calc(100% + 6px)',
                    right: 0,
                    width: '210px',
                    background: '#ffffff',
                    border: '1px solid #e2e8f0',
                    borderRadius: '14px',
                    padding: '6px',
                    boxShadow: '0 10px 25px rgba(0,0,0,0.1)',
                    zIndex: 100,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '4px'
                  }}
                >
                  {[
                    { key: 'recently', label: 'Recently Added' },
                    { key: 'price-low', label: 'Price Low to High' },
                    { key: 'price-high', label: 'Price High to Low' }
                  ].map((option) => (
                    <button
                      key={option.key}
                      onClick={() => {
                        setSortBy(option.key);
                        setSortDropdownOpen(false);
                      }}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        padding: '10px 12px',
                        borderRadius: '8px',
                        border: 'none',
                        background: sortBy === option.key ? '#fef2f2' : 'transparent',
                        color: sortBy === option.key ? '#dc2626' : '#0f172a',
                        fontSize: '13px',
                        fontWeight: sortBy === option.key ? 800 : 600,
                        cursor: 'pointer',
                        textAlign: 'left'
                      }}
                    >
                      <span>{option.label}</span>
                      {sortBy === option.key && <Check size={16} color="#dc2626" />}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Grid vs List View Toggle Icons */}
            <div style={{ display: 'flex', background: '#f1f5f9', borderRadius: '10px', padding: '3px' }}>
              <button
                onClick={() => setViewMode('grid')}
                style={{
                  background: viewMode === 'grid' ? '#ffffff' : 'transparent',
                  border: 'none',
                  borderRadius: '8px',
                  padding: '8px 10px',
                  color: viewMode === 'grid' ? '#dc2626' : '#64748b',
                  cursor: 'pointer',
                  boxShadow: viewMode === 'grid' ? '0 2px 5px rgba(0,0,0,0.05)' : 'none'
                }}
                title="Grid View"
              >
                <LayoutGrid size={17} />
              </button>

              <button
                onClick={() => setViewMode('list')}
                style={{
                  background: viewMode === 'list' ? '#ffffff' : 'transparent',
                  border: 'none',
                  borderRadius: '8px',
                  padding: '8px 10px',
                  color: viewMode === 'list' ? '#dc2626' : '#64748b',
                  cursor: 'pointer',
                  boxShadow: viewMode === 'list' ? '0 2px 5px rgba(0,0,0,0.05)' : 'none'
                }}
                title="List View"
              >
                <List size={17} />
              </button>
            </div>

          </div>
        </div>

        {/* SAVED ITEMS CONTAINER */}
        {loading ? (
          <div style={{ padding: '60px', textAlign: 'center', color: '#64748b', fontSize: '15px' }}>Loading your saved collection...</div>
        ) : sortedItems.length === 0 ? (
          <div style={{ background: 'transparent', padding: '60px 20px', textAlign: 'center' }}>
            <Heart size={48} color="#dc2626" style={{ margin: '0 auto 16px' }} />
            <h3 style={{ fontSize: '20px', fontWeight: 900, color: '#0f172a', marginBottom: '8px' }}>Your collection is empty</h3>
            <p style={{ fontSize: '14px', color: '#64748b', maxWidth: '400px', margin: '0 auto 24px' }}>
              Explore our range of red light panels and save your favorite models here.
            </p>
            <Link to="/shop" style={{ background: '#dc2626', color: '#ffffff', padding: '12px 28px', borderRadius: '9999px', fontSize: '14px', fontWeight: 800, textDecoration: 'none', display: 'inline-block' }}>
              Browse Products
            </Link>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
            {viewMode === 'list' ? (
              
              /* 1. LIST VIEW */
              sortedItems.map((p) => {
                const cartItem = cartItems.find((ci) => Number(ci.id) === Number(p.id));
                const currentQty = cartItem ? (cartItem.quantity || 1) : 0;
                const availableStock = p.stock !== undefined && p.stock !== null ? p.stock : 99;

                return (
                  <div
                    key={p.id}
                    style={{
                      background: '#ffffff',
                      border: '1px solid #e2e8f0',
                      borderRadius: '20px',
                      padding: '24px 32px',
                      display: 'grid',
                      gridTemplateColumns: '180px 1fr 140px 200px',
                      gap: '32px',
                      alignItems: 'center',
                      boxShadow: '0 4px 15px rgba(0,0,0,0.02)',
                      position: 'relative'
                    }}
                  >
                    {/* Left Column: Product Image */}
                    <Link to={`/product/${p.id}`} style={{ height: '150px', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f8fafc', borderRadius: '14px', padding: '12px', textDecoration: 'none' }}>
                      <img
                        src={getProductImageUrl(p.image_url)}
                        alt={p.name}
                        style={{ maxHeight: '100%', maxWidth: '100%', objectFit: 'contain', display: 'block' }}
                        onError={(e) => { e.target.src = getProductImageUrl('/uploads/panel_72led.jpg'); }}
                      />
                    </Link>

                    {/* Middle Column: Title & Specs */}
                    <div>
                      <Link to={`/product/${p.id}`} style={{ textDecoration: 'none' }}>
                        <h3 style={{ fontSize: '20px', fontWeight: 900, color: '#0f172a', marginBottom: '8px' }}>
                          {p.name}
                        </h3>
                      </Link>

                      {/* Spec Line */}
                      <div style={{ fontSize: '13px', fontWeight: 700, color: '#64748b', marginBottom: '10px' }}>
                        <span>{p.led_chips_count || 144} LEDs</span>
                        <span style={{ margin: '0 8px' }}>•</span>
                        <span>{p.spectrum || "660nm + 850nm"}</span>
                        <span style={{ margin: '0 8px' }}>•</span>
                        <span>{p.actual_power || "240W"}</span>
                      </div>

                      <p style={{ fontSize: '13.5px', color: '#475569', lineHeight: 1.5, marginBottom: '12px' }}>
                        {p.description || "Advanced Red Light Therapy panel engineered with dual-chip 660nm Red and 850nm Near-Infrared wavelengths."}
                      </p>

                      {/* Stock Status Pill */}
                      <div style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', fontSize: '12px', fontWeight: 800, color: '#16a34a', background: '#dcfce7', padding: '4px 12px', borderRadius: '9999px' }}>
                        <span style={{ width: '6px', height: '6px', borderRadius: '50%', background: '#16a34a' }}></span>
                        <span>In Stock</span>
                      </div>
                    </div>

                    {/* Price Tag Column */}
                    <div style={{ textAlign: 'right' }}>
                      <div style={{ fontSize: '26px', fontWeight: 900, color: '#0f172a' }}>
                        £{Number(p.price || 350).toFixed(2)}
                      </div>
                    </div>

                    {/* Right Actions Column */}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', alignItems: 'flex-end', justifyContent: 'center', width: '100%' }}>
                      
                      {/* ADD TO CART BUTTON OR - QTY IN CART + SELECTOR */}
                      {currentQty <= 0 ? (
                        <button
                          onClick={() => onAddToCart && onAddToCart(p)}
                          style={{
                            width: '100%',
                            background: '#dc2626',
                            color: '#ffffff',
                            border: 'none',
                            borderRadius: '10px',
                            padding: '12px 18px',
                            fontSize: '13.5px',
                            fontWeight: 800,
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '8px',
                            boxShadow: '0 4px 12px rgba(220, 38, 38, 0.3)'
                          }}
                        >
                          <ShoppingBag size={16} />
                          <span>Add to Cart</span>
                        </button>
                      ) : (
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '10px', padding: '4px 8px', width: '100%' }}>
                          <button
                            onClick={() => onUpdateQuantity && onUpdateQuantity(p.id, -1, availableStock)}
                            style={{ width: '28px', height: '28px', borderRadius: '6px', background: '#ffffff', border: '1px solid #fecaca', color: '#dc2626', fontWeight: 900, fontSize: '15px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                            title="Decrease quantity"
                          >
                            -
                          </button>
                          <span style={{ fontSize: '13px', fontWeight: 900, color: '#dc2626' }}>
                            {currentQty} in Cart
                          </span>
                          <button
                            onClick={() => onUpdateQuantity && onUpdateQuantity(p.id, 1, availableStock)}
                            disabled={currentQty >= availableStock}
                            style={{ width: '28px', height: '28px', borderRadius: '6px', background: currentQty >= availableStock ? '#f1f5f9' : '#dc2626', border: 'none', color: currentQty >= availableStock ? '#94a3b8' : '#ffffff', fontWeight: 900, fontSize: '15px', cursor: currentQty >= availableStock ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                            title="Increase quantity"
                          >
                            +
                          </button>
                        </div>
                      )}

                      {/* Remove Trash Icon Button */}
                      <button
                        onClick={() => handleRemove(p.id)}
                        style={{
                          width: '100%',
                          background: '#f8fafc',
                          border: '1px solid #e2e8f0',
                          borderRadius: '10px',
                          padding: '9px 18px',
                          fontSize: '12.5px',
                          fontWeight: 700,
                          color: '#64748b',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: '6px'
                        }}
                      >
                        <Trash2 size={15} color="#94a3b8" />
                        <span>Remove</span>
                      </button>

                    </div>
                  </div>
                );
              })
            ) : (
              
              /* 2. GRID VIEW */
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '24px' }}>
                {sortedItems.map((p) => {
                  const cartItem = cartItems.find((ci) => Number(ci.id) === Number(p.id));
                  const currentQty = cartItem ? (cartItem.quantity || 1) : 0;
                  const availableStock = p.stock !== undefined && p.stock !== null ? p.stock : 99;

                  return (
                    <div
                      key={p.id}
                      style={{
                        background: '#ffffff',
                        border: '1px solid #e2e8f0',
                        borderRadius: '20px',
                        padding: '24px',
                        boxShadow: '0 4px 15px rgba(0,0,0,0.02)',
                        display: 'flex',
                        flexDirection: 'column',
                        position: 'relative'
                      }}
                    >
                      <Link to={`/product/${p.id}`} style={{ height: '180px', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '16px' }}>
                        <img src={getProductImageUrl(p.image_url)} alt={p.name} style={{ maxHeight: '100%', maxWidth: '100%', objectFit: 'contain' }} />
                      </Link>

                      <h3 style={{ fontSize: '18px', fontWeight: 900, color: '#0f172a', marginBottom: '6px' }}>{p.name}</h3>
                      <div style={{ fontSize: '12.5px', color: '#64748b', marginBottom: '12px' }}>{p.actual_power || '240W'} • {p.spectrum || '660nm + 850nm'}</div>
                      <div style={{ fontSize: '22px', fontWeight: 900, color: '#0f172a', marginBottom: '16px', marginTop: 'auto' }}>£{Number(p.price || 350).toFixed(2)}</div>

                      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                        {currentQty <= 0 ? (
                          <button
                            onClick={() => onAddToCart && onAddToCart(p)}
                            style={{ width: '100%', background: '#dc2626', color: '#ffffff', border: 'none', borderRadius: '10px', padding: '11px', fontSize: '13px', fontWeight: 800, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }}
                          >
                            <ShoppingBag size={15} />
                            <span>Add to Cart</span>
                          </button>
                        ) : (
                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '10px', padding: '4px 8px', width: '100%' }}>
                            <button
                              onClick={() => onUpdateQuantity && onUpdateQuantity(p.id, -1, availableStock)}
                              style={{ width: '28px', height: '28px', borderRadius: '6px', background: '#ffffff', border: '1px solid #fecaca', color: '#dc2626', fontWeight: 900, fontSize: '15px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                            >
                              -
                            </button>
                            <span style={{ fontSize: '13px', fontWeight: 900, color: '#dc2626' }}>
                              {currentQty} in Cart
                            </span>
                            <button
                              onClick={() => onUpdateQuantity && onUpdateQuantity(p.id, 1, availableStock)}
                              disabled={currentQty >= availableStock}
                              style={{ width: '28px', height: '28px', borderRadius: '6px', background: currentQty >= availableStock ? '#f1f5f9' : '#dc2626', border: 'none', color: currentQty >= availableStock ? '#94a3b8' : '#ffffff', fontWeight: 900, fontSize: '15px', cursor: currentQty >= availableStock ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                            >
                              +
                            </button>
                          </div>
                        )}

                        <button
                          onClick={() => handleRemove(p.id)}
                          style={{ width: '100%', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '10px', padding: '8px', color: '#64748b', fontSize: '12.5px', fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }}
                        >
                          <Trash2 size={14} color="#94a3b8" />
                          <span>Remove</span>
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

      </div>
    </div>
  );
};

export default UserWishlist;
